<script setup lang="ts">
import { lastValueFrom } from 'rxjs';
import { computed, ref, watch } from 'vue';
import { getPlanPriceForUser, subscriptionBenefitPercentage } from '@kcalc/lib/browser';
import { upgradeSubscription$ } from '@/lib/firebase/functions';
import BaseButton from '@/components/BaseButton.vue';
import { usePayment } from '@/composables/firebase/payment';
import UpgradeIcon from '@/components/payment/UpgradeIcon.vue';
import { useSystemMessage } from '@/composables/system-message';
import { useAuth } from '@/composables/firebase/auth';
import { useGamification } from '@/composables/gamification';

const emit = defineEmits(['close', 'isRunning']);

const { setSystemMessageByCode } = useSystemMessage();
const { subscription, subscriptionPlan } = usePayment();
const { user } = useAuth();

const isUpgradeRunning = ref(false);

watch(isUpgradeRunning, (val) => {
  emit('isRunning', val);
});

const monthlyPrice = computed(() => {
  if (subscriptionPlan.value && user.value?.profile) {
    return getPlanPriceForUser(subscriptionPlan.value, 'yearly', user.value.profile, true, false, false)
      .toFixed(2).replace('.', ',').replace(',00', '');
  }

  return undefined;
});

const handleYearlyUpgradeClick = async () => {
  isUpgradeRunning.value = true;

  await lastValueFrom(upgradeSubscription$({ plan: subscription.value?.plan ?? 'basic', period: 'yearly' }))
    .then((response) => {
      isUpgradeRunning.value = false;
      if (response) {
        location.href = response;
      }
    })
    .catch((e) => {
      setSystemMessageByCode(e.message);
      isUpgradeRunning.value = false;
      emit('close');
    });
};

const { redeemedLevel } = useGamification();
const benefit = computed(() => (
  redeemedLevel.value > 0
    ? subscriptionBenefitPercentage[redeemedLevel.value]
    : 0
));
const hasBenefit = computed(() => benefit.value > 0);

const yearlyPriceInfo = computed(() => {
  if (subscriptionPlan.value && user.value?.profile) {
    const monthlyPrice = getPlanPriceForUser(subscriptionPlan.value, 'monthly', user.value.profile, true, false) * 12;
    const yearlyPrice = getPlanPriceForUser(subscriptionPlan.value, 'yearly', user.value.profile, true, false, true);

    return {
      monthlyPrice: monthlyPrice.toFixed(2).replace('.', ',').replace(',00', ''),
      yearlyPrice: yearlyPrice.toFixed(2).replace('.', ',').replace(',00', ''),
    };
  }

  return null;
});

</script>

<template>
  <div class="yearly-upgrade-content">
    <UpgradeIcon with-background />

    <h1>
      {{ $t('upgrade.yearlyUpgradeContent.headline', { plan: subscriptionPlan?.name }) }}
    </h1>

    <p class="description">
      {{ $t('upgrade.yearlyUpgradeContent.description', { plan: subscriptionPlan?.name }) }}
    </p>

    <div class="recommendation">
      <div>
        <font-awesome-icon :icon="['far', 'check-circle']" />
        {{ $t('upgrade.yearlyUpgradeContent.recommendation') }}
      </div>
      <div class="price">
        <span class="amount">{{ monthlyPrice }} €</span> <span class="period">{{ $t('SubscriptionPlans.monthly') }}</span>

        <div
          v-if="yearlyPriceInfo"
          class="yearly-payment-info">
          <span>{{ yearlyPriceInfo.monthlyPrice }} €</span>
          <span class="ml-1">{{ yearlyPriceInfo.yearlyPrice }} €</span>
          {{ $t('SubscriptionPlans.perYear') }}
        </div>

        <div
          v-if="hasBenefit"
          class="benefit">
          ({{ $t('SubscriptionPlans.benefitInfo', { benefit, level: redeemedLevel }) }})
        </div>
      </div>
    </div>

    <div class="action">
      <BaseButton
        class="btn-primary"
        :disabled="isUpgradeRunning"
        :loading="isUpgradeRunning"
        @click="handleYearlyUpgradeClick">
        {{ $t('upgrade.yearlyUpgradeContent.cta') }}
      </BaseButton>
    </div>

    <p class="vat-info">
      {{ $t('SubscriptionPlans.vatInfo') }}
    </p>
  </div>
</template>

<style>
.yearly-upgrade-content {
  text-align: center;

  .upgrade-icon {
    --size: 5rem;
    margin: var(--space-4) auto var(--space-4) auto;
  }

  h1 {
    margin-bottom: var(--space-3);
  }

  .description {
    font-size: var(--font-size-default);
    line-height: var(--line-height-m);
  }

  .recommendation {
    background: var(--color-primary-ultra-light);
    padding: var(--space-3);
    border-radius: var(--border-radius);
    max-width: 600px;
    margin: var(--space-3) auto 0;
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: var(--font-size-m);

    > div:first-child {
      display: flex;
      align-items: center;
    }

    .price {
      .amount {
        font-weight: var(--font-weight-bold);
        font-size: var(--font-size-xl);
      }

      .period {
        font-size: var(--font-size-xs);
      }

      .yearly-hint {
        font-size: var(--font-size-xs);
      }

      .benefit {
        font-size: var(--font-size-xs);
        margin-top: calc(var(--space-1) / 2);
      }
    }

    .yearly-payment-info {
      margin-top: var(--space-1);
      font-size: var(--font-size-s);

      span {
        &:first-child {
          color: var(--color-danger);
          text-decoration: line-through;
        }
      }
    }

    svg {
      font-size: var(--font-size-xl);
      margin-right: var(--space-2);
    }
  }

  .action {
    margin: var(--space-5) 0 var(--space-2);
  }

  .vat-info {
    font-size: var(--font-size-s);
    line-height: var(--line-height-m);
  }
}
</style>
